// 入力フォームの初期値 (initialValues属性にセットする)
import { PagePath } from 'constants/pagePath';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastMessageLevel } from 'views/mutual/toast';
import { useToast } from 'views/mutual/toast/hooks';
import { SendSMSParam } from 'views/templates/sendSMS';
import { authenticationCodeValidation } from 'views/utils/commonValidation';
import { useReissuePasswordMutation, useSendSmsMutation, useSmsCertificateMutation } from 'views/utils/graphql/graphql';
import useOnceSubmit from 'views/utils/useOnceSubmit';
import { useCorporateAccountEstablishStore, useUpdateStore } from 'views/templates/accountOpen/hooks';
import { TemplateProps } from '../common';
import { PageStep } from 'views/pages/accountInfo/hooks';
import { useGlobalToastHandler } from 'views/mutual/globalToast/hooks';
import { DateFormatType, formatDate } from 'views/utils/converter';
import {
  findCorporateBusinessContentId,
  findInvestorCategoryInfoId,
  findRelastionShipName,
  findSettlementDayId,
  findSettlementMonthId,
  findCorporateLeaseFundPurposeId,
  findDomesticRealEstatePurposeId,
  findCorporateOverseasRealEstatePurposeId,
  ChangeAccountInfoExecuteType,
  FatcaStatus,
} from 'views/utils/enums';
import { AboutRulerType, RelastionShipType, RulerImportantPublicWorkerType } from '../rulerInfo/hooks';
import { AuthModeSelectType } from '../tfAuthentication/hooks';
import { ChangeAccountInfoMutationVariables, useChangeAccountInfoMutation } from 'views/utils/graphql/graphql';

export type LoginParamType = {
  account: string;
  Email: string;
};

export const useUpdateMail = ({ isUpdate, setPageStep }: TemplateProps) => {
  const [provisionalToken, setProvisionalToken] = useState<string>('');
  const [sendSMSData, sendSMS] = useSendSmsMutation();
  const { toastProps, showToast } = useToast();
  const { add, param, clear } = useCorporateAccountEstablishStore();
  const { onSendSMS } = useSendEmail(provisionalToken, setProvisionalToken, showToast);
  const certificatePage = useCertificatePage(provisionalToken, showToast, setPageStep);

  useEffect(() => {
    const id = setTimeout(() => {
      void sendEmail();
    }, 0);
    return () => clearTimeout(id);
  }, []);

  const sendEmail = async () => {
    const ret = await sendSMS({
      input: {
        Type: 5,
        Email: param.dealerMailAddress,
      },
    });

    if (ret.data && ret.data?.SendSMS?.Code !== 0) {
      showToast(ret.data?.SendSMS?.Message ?? '認証コードの送信に失敗しました', ToastMessageLevel.Error);
    } else if (ret.data?.SendSMS?.Code === 0) {
      setProvisionalToken(ret.data.SendSMS.ProvisionalToken!);
    }
  };

  const goBackPage = () => {
    setPageStep && setPageStep(PageStep.CONFIRM);
  };

  return { toastProps, certificatePage: { onSendSMS, ...certificatePage }, goBackPage };
};

const useSendEmail = (
  initialProvisionalToken: string,
  setProvisionalToken: React.Dispatch<React.SetStateAction<string>>,
  onShowToast: (label: string, mesageLevel: ToastMessageLevel) => void,
) => {
  const [sendSMSData, sendSMS] = useSendSmsMutation();
  const { add, param } = useCorporateAccountEstablishStore();
  const [newMailAddress] = useState(param.dealerMailAddress);
  const onSendSMS = useOnceSubmit<void>(
    {
      onSubmit: async () => {
        const res = await sendSMS({
          input: {
            Type: 5,
            Email: newMailAddress,
          },
        });

        if (res.data && res.data?.SendSMS?.Code !== 0) {
          onShowToast(res.data?.SendSMS?.Message ?? '認証コードの送信に失敗しました', ToastMessageLevel.Error);
        } else if (res.data?.SendSMS?.Code === 0) {
          setProvisionalToken(res.data.SendSMS.ProvisionalToken!);
          onShowToast('認証コードを送信しました', ToastMessageLevel.Info);
        }
      },
    },
    [sendSMS, initialProvisionalToken, onShowToast, setProvisionalToken],
  );

  return {
    onSendSMS,
  };
};

const useCertificatePage = (
  provisionalToken: string,
  showToast: (label: string, mesageLevel: ToastMessageLevel) => void,
  setPageStep?: React.Dispatch<React.SetStateAction<PageStep>>,
) => {
  const [smsCertificatelData, smsCertificate] = useSmsCertificateMutation();
  const { add, param, clear } = useCorporateAccountEstablishStore();
  const { showError } = useGlobalToastHandler();
  const [_changeAccountResponse, changeAccount] = useChangeAccountInfoMutation();

  const validate = (values: SendSMSParam) => {
    const errors: Partial<{
      authenticationCode: string;
    }> = {};

    const codeError = authenticationCodeValidation(values.authenticationCode);
    if (codeError) {
      errors.authenticationCode = codeError;
    }

    return errors;
  };

  const smsCertificateSubmit = useOnceSubmit<SendSMSParam>(
    {
      onSubmit: async (values) => {
        const ret = await smsCertificate({
          input: {
            Type: 5,
            ProvisionalToken: provisionalToken,
            AuthenticationCode: values.authenticationCode,
          },
        });

        if (ret.data && ret.data.SMSCertificate?.Code !== 0) {
          showToast(ret.data.SMSCertificate?.Message ?? '認証に失敗しました', ToastMessageLevel.Error);
        } else {
          await executeUpdateAccount();
        }
      },
    },
    [smsCertificate, showToast],
  );

  const executeUpdateAccount = async () => {
    const data: ChangeAccountInfoMutationVariables = {
      input: {
        HaveEKYCDocument: param.identificationQuestion,
        CanTakePicture: param.selfieQuestion,
        ConfirmResponsibility: param.confirmResponsibility,
        CorporateNumber: param.corporateNumber,
        Name: param.corporateName,
        Furigana: param.corporateFurigana,
        PostCode: param.corporatePostCode,
        Prefecture: param.corporatePrefecture,
        CityCountry: param.corporateCityCountry,
        TownStreetAddress: param.corporateTownStreetAddress,
        BuildingNameAndRoomNumber: param.corporateBuildingNameAndRoomNumber,
        RegisteredPostCode: param.registeredCorporatePostCode,
        RegisteredPrefecture: param.registeredCorporatePrefecture,
        RegisteredCityCountry: param.registeredCorporateCityCountry,
        RegisteredTownStreetAddress: param.registeredCorporateTownStreetAddress,
        RegisteredBuildingNameAndRoomNumber: param.registeredCorporateBuildingNameAndRoomNumber,
        CorporateSettlementMonth: [findSettlementMonthId(param.corporateSettlementMonth)!],
        CorporateSettlementDay: findSettlementDayId(param.corporateSettlementDay),
        CorporateEstablishmentDate: formatDate(param.corporateEstablishmentDate, DateFormatType.Slash_yyyyMMdd),
        CorporateMainBusinessContent: findCorporateBusinessContentId(param.corporateMainBusinessContent),
        CorporateMainBusinessContentOtherText: param.corporateMainBusinessContentOtherText,
        CorporateCapital: Number(param.corporateCapital),
        CorporateNumberOfEmployees: Number(param.corporateNumberOfEmployees),
        CorporateHomepage: param.corporateHomepage,
        LeaseFundPurpose: param.leaseFundPurpose
          .filter((x) => x.checked)
          .map((x) => findCorporateLeaseFundPurposeId(x.label) ?? null),
        LeaseFundPurposeOtherText: param.leaseFundPurposeOtherText,
        DomesticRealEstatePurpose: param.domesticRealEstatePurpose
          .filter((x) => x.checked)
          .map((x) => findDomesticRealEstatePurposeId(x.label) ?? null),
        DomesticRealEstatePurposeOtherText: param.domesticRealEstatePurposeOtherText,
        OverseasRealEstatePurpose: param.overseasRealEstatePurpose
          .filter((x) => x.checked)
          .map((x) => findCorporateOverseasRealEstatePurposeId(x.label) ?? null),
        OverseasRealEstatePurposeOtherText: param.overseasRealEstatePurposeOtherText,
        ContactPhoneNumber: param.contactPhoneNumber,
        ContactFaxNumber: param.contactFaxNumber,
        RepresentFirstName: param.representFirstName,
        RepresentLastName: param.representLastName,
        RepresentFirstNameKana: param.representFirstNameKana,
        RepresentLastNameKana: param.representLastNameKana,
        RepresentBirthDay: formatDate(param.representBirthDay, DateFormatType.Slash_yyyyMMdd),
        RepresentPostCode: param.representPostCode,
        RepresentPrefecture: param.representPrefecture,
        RepresentCityCountry: param.representCityCountry,
        RepresentTownStreetAddress: param.representTownStreetAddress,
        RepresentBuildingNameAndRoomNumber: param.representBuildingNameAndRoomNumber,
        RepresentMobilePhoneNumber: param.representMobilePhoneNumber,
        RepresentMailAddress: param.representMailAddress,
        RepresentPosition: param.representPosition,
        DealerFirstName: param.dealerFirstName,
        DealerLastName: param.dealerLastName,
        DealerFirstNameKana: param.dealerFirstNameKana,
        DealerLastNameKana: param.dealerLastNameKana,
        DealerBirthDay: formatDate(param.dealerBirthDay, DateFormatType.Slash_yyyyMMdd),
        DealerPostCode: param.dealerPostCode,
        DealerPrefecture: param.dealerPrefecture,
        DealerCityCountry: param.dealerCityCountry,
        DealerTownStreetAddress: param.dealerTownStreetAddress,
        DealerBuildingNameAndRoomNumber: param.dealerBuildingNameAndRoomNumber,
        DealerMobilePhoneNumber: param.dealerMobilePhoneNumber,
        DealerMailAddress: param.dealerMailAddress,
        DealerDepartment: param.dealerDepartment,
        DealerOtherDepartment: param.dealerOtherDepartment,
        DealerPosition: param.dealerPosition,
        AboutRuler1: param.aboutRuler1 === AboutRulerType.None ? null : param.aboutRuler1,
        Ruler1Relationship: param.ruler1Relationship === RelastionShipType.None ? null : param.ruler1Relationship,
        Ruler1Name: param.ruler1Name,
        Ruler1BirthDay: formatDate(param.ruler1BirthDay, DateFormatType.Slash_yyyyMMdd),
        Ruler1MailAddress: param.ruler1MailAddress,
        Ruler1PostCode: param.ruler1PostCode,
        Ruler1Prefecture: param.ruler1Prefecture,
        Ruler1CityCountry: param.ruler1CityCountry,
        Ruler1TownStreetAddress: param.ruler1TownStreetAddress,
        Ruler1BuildingNameAndRoomNumber: param.ruler1BuildingNameAndRoomNumber,
        Ruler1ImportantPublicWorker:
          param.ruler1ImportantPublicWorker === RulerImportantPublicWorkerType.None
            ? null
            : param.ruler1ImportantPublicWorker === RulerImportantPublicWorkerType.Yes,
        AboutRuler2: param.aboutRuler2 === AboutRulerType.None ? null : param.aboutRuler2,
        Ruler2Relationship: param.ruler2Relationship === RelastionShipType.None ? null : param.ruler2Relationship,
        Ruler2Name: param.ruler2Name,
        Ruler2BirthDay: formatDate(param.ruler2BirthDay, DateFormatType.Slash_yyyyMMdd),
        Ruler2MailAddress: param.ruler2MailAddress,
        Ruler2PostCode: param.ruler2PostCode,
        Ruler2Prefecture: param.ruler2Prefecture,
        Ruler2CityCountry: param.ruler2CityCountry,
        Ruler2TownStreetAddress: param.ruler2TownStreetAddress,
        Ruler2BuildingNameAndRoomNumber: param.ruler2BuildingNameAndRoomNumber,
        Ruler2ImportantPublicWorker:
          param.ruler2ImportantPublicWorker === RulerImportantPublicWorkerType.None
            ? null
            : param.ruler2ImportantPublicWorker === RulerImportantPublicWorkerType.Yes,
        AboutRuler3: param.aboutRuler3 === AboutRulerType.None ? null : param.aboutRuler3,
        Ruler3Relationship: param.ruler3Relationship === RelastionShipType.None ? null : param.ruler3Relationship,
        Ruler3Name: param.ruler3Name,
        Ruler3BirthDay: formatDate(param.ruler3BirthDay, DateFormatType.Slash_yyyyMMdd),
        Ruler3MailAddress: param.ruler3MailAddress,
        Ruler3PostCode: param.ruler3PostCode,
        Ruler3Prefecture: param.ruler3Prefecture,
        Ruler3CityCountry: param.ruler3CityCountry,
        Ruler3TownStreetAddress: param.ruler3TownStreetAddress,
        Ruler3BuildingNameAndRoomNumber: param.ruler3BuildingNameAndRoomNumber,
        Ruler3ImportantPublicWorker:
          param.ruler3ImportantPublicWorker === RulerImportantPublicWorkerType.None
            ? null
            : param.ruler3ImportantPublicWorker === RulerImportantPublicWorkerType.Yes,
        InvestorCategoryInfo: param.investorCategoryInfo
          .filter((x) => x.checked)
          .map((x) => findInvestorCategoryInfoId(x.label) ?? null),
        TwoFactorAuthenticationType: param.authMode ?? AuthModeSelectType.None,
        Fatca: param.fatca,
        TransferBankName: param.bankName,
        TransferBranchName: param.branchName,
        TransferAccountType: param.accountType,
        TransferAccountNumber: param.accountNumber,
        TransferAccountHolderName: param.accountHolderName,
        InvoiceNumber: param.invoiceNumber,
        HasNoInvoiceNumber: param.hasNoInvoiceNumber,
      },
    };

    const changeAccountData = await changeAccount(data);
    if (!changeAccountData?.data) return;

    if (changeAccountData.data.ChangeAccountInfo?.Code === 0) {
      setPageStep && setPageStep(PageStep.COMPLETE);
    } else {
      showError(changeAccountData.data.ChangeAccountInfo?.Message);
    }
  };

  const formik = useFormik<SendSMSParam>({
    initialValues: {
      authenticationCode: '',
    },
    onSubmit: smsCertificateSubmit,
    validate,
    validateOnBlur: false,
    validateOnChange: false,
  });

  useEffect(() => {
    formik.setFieldValue('authenticationCode', '');
  }, []);

  const maskMailAddress = (addr?: string | null) => {
    if (!addr) return '';
    const head = addr.slice(0, 4);
    const tail = addr.length >= 4 ? addr.slice(-4) : '';
    return `メールアドレス${head}********${tail}宛に送信された認証コードを入力してください`;
  };

  return { formik, text: maskMailAddress(param.dealerMailAddress), isUpdate: false };
};
